/**
 * 🔦 Light — how far a torch reaches, and what stops it.
 *
 * **T128 · D90 · [`Minecraft/14_Light.md`](../../../Development/Minecraft/14_Light.md)**
 *
 * ## 🎯 Eight metres, and the number is haugur's
 *
 * **2026-08-31, asked how far a torch should light: *8 metres*.**
 *
 * 🔴 **Minecraft's torch is 14 and `Minecraft/14` §7.5 says in as many words not
 * to copy it:** *"Sixteen levels is a storage decision Minecraft made when four
 * bits was the budget… the number of levels should follow from how far a fire
 * should light. Do not decide this by copying. It is the one number in this
 * document that is pure flavour, and it is the one most likely to be copied on
 * sight."*
 *
 * ⚠️ **At 1 block = 1 m, Minecraft's 14 would light most of a farmstead from one
 * stick.** Eight is the width of a room — **haugur's own number for a room**,
 * the one `shelter.ts` already bounds the escape search by (D84).
 *
 * ## What this file does and does not do
 *
 * ✅ **Block light only.** Light from things that burn.
 *
 * ⬜ **There is no SKY light here, and that is deliberate.** `Minecraft/14` §1
 * makes two values load-bearing — block light and sky light, with the renderer
 * taking the larger — and §7.4 says sky light for an open, caveless landscape is
 * nearly a heightmap question. **Ours is a bigger job than the torch, and the
 * torch does not need it**: this light is *added* to a world the sun already
 * lights, so nothing goes dark for want of it.
 *
 * ## 🔴 Removal is FREE here, and that is the whole reason it is shaped this way
 *
 * §4 measures Minecraft's light removal at up to **44× the block reads** of
 * adding light, and calls it structural: *"when a light is taken away you cannot
 * simply subtract — every darkened position has to be re-examined to find out
 * whether some other light was propping it up."*
 *
 * ✅ **That cost belongs to an INCREMENTAL light map, and we do not keep one.**
 * The light for a region is computed from scratch whenever that region is
 * rebuilt — which already happens on every block edit, because the mesh has to
 * be rebuilt anyway. **Breaking a torch is therefore exactly as cheap as placing
 * one**, and the expensive half of §4 never arrives.
 *
 * ⚠️ **What that costs instead is a recomputation on every edit**, bounded and
 * predictable rather than cascading. 🔴 **It has NOT been measured** — there is
 * no GPU and no game on the machine this was written on, and `Minecraft/14` §8
 * is explicit that *"nothing here is measured on our own renderer."* **Measure
 * it on haugur's Lenovo before trusting it at scale.**
 */

import { blockCutout, blockHeight, isFluid, isSolid, blockBehaviour, BLOCK_AIR } from './blocks.ts';
import { stateOverride } from './states.ts';

/**
 * The brightest anything may be.
 *
 * ⚠️ **A storage ceiling, not a design number.** Nothing emits 15 today and
 * nothing need ever: the torch is 8 because that is how far haugur wants it to
 * reach. This is here so a brighter thing — a hearth, `kvika` — has somewhere to
 * go without every stored value changing meaning.
 */
export const MAX_LIGHT = 15;

/**
 * How much light this block gives off, 0–15.
 *
 * ## 🔴 It asks the STATE first, and that order is the whole design
 *
 * **Two files can hold a light value and they answer different questions.**
 *
 * | | |
 * |---|---|
 * | `block-states.json`'s `perState` | *this exact state glows* — **a LIT hearth**, and not a cold one |
 * | `block-behaviour.json` | *this block glows in every state it has* — **a torch**, which burns for ever |
 *
 * 🔴 **The state has to win, and if it did not the hearth would be impossible.**
 * Lit and unlit `eldstaedi` share a block key, so a number in
 * `block-behaviour.json` would light **every burnt-out fire in Iceland** — the
 * fault that file predicted in writing, months before anything needed it, and
 * refused to introduce.
 *
 * ⚠️ **A torch's 8 is NOT in the state file**, deliberately: all five of its
 * facings are lit, so its brightness is a property of the block. Putting it in
 * `perState` would mean five identical entries and five chances to disagree.
 */
export function lightFrom(id: number): number {
  const perState = stateOverride(id)?.light;
  if (typeof perState === 'number' && perState > 0) {
    return Math.min(MAX_LIGHT, Math.floor(perState));
  }
  const light = blockBehaviour(id)?.light;
  return typeof light === 'number' && light > 0 ? Math.min(MAX_LIGHT, Math.floor(light)) : 0;
}

/**
 * 🔴 **Does light stop here?** — the sixth question `solid` used to answer by
 * accident, and it gets its own name for the reason all five others did.
 *
 * **Only a whole opaque cube stops light.** Everything else passes it:
 *
 * | | |
 * |---|---|
 * | air | ✅ passes |
 * | a torch | ✅ passes — it is a post 0.125 m square, and a light that its own post blocked would light nothing |
 * | `kjarr`, a hearth | ✅ passes — sub-metre, so there is a gap above them |
 * | leaves | ✅ passes — the tile is part hole, and `sheltersFromWeather` already says so |
 * | water | ✅ passes ⚠️ **and it should FILTER** — `Minecraft/14` §2 costs it one extra level per block, *"which is exactly why a lake dims with depth."* Not built; nothing is lit underwater yet |
 * | móberg, turf, planks, anything built | ❌ stops |
 */
export function passesLight(id: number): boolean {
  if (id === BLOCK_AIR) return true;
  if (isFluid(id)) return true;
  if (blockCutout(id) > 0) return true;
  if (blockHeight(id) < 1) return true;
  return !isSolid(id);
}

/**
 * 🔦 **Flood light through a cube of blocks.**
 *
 * @param ids the region's block numbers, `size³` of them, indexed
 *   `x + size * (z + size * y)` — **the same layout a chunk uses**, so a caller
 *   filling this from chunks does not have to think twice.
 * @param size the cube's edge length.
 * @returns one light level per cell, or `null` if **nothing in the region emits
 *   any** — which is the ordinary case and is worth not allocating for.
 *
 * ## How it spreads — `Minecraft/14` §2, and it is load-bearing
 *
 * **One level per block of taxicab distance**, through the six face neighbours.
 * A torch at 8 is 8 in its own cell, 7 one block away, and dark at 8. **It is a
 * diamond, not a sphere**, and that is Minecraft's own shape rather than a
 * simplification of it.
 *
 * ## ⚠️ Why a breadth-first PUSH and not a per-cell pull
 *
 * §4 measured the difference and it is not small: vanilla recalculates a queued
 * cell **from all six neighbours** — 171,739 reads against Starlight's 24,535,
 * about **7×**. ✅ **Pushing outward and queueing only what got brighter is the
 * cheap direction**, and it is barely more code.
 */
export function floodLight(ids: Uint16Array, size: number): Uint8Array | null {
  const emitters = findEmitters(ids, size);
  if (emitters.length === 0) return null;

  const inside = (x: number, y: number, z: number): boolean =>
    x >= 0 && y >= 0 && z >= 0 && x < size && y < size && z < size;

  const lit = floodFrom(emitters, (x, y, z) =>
    inside(x, y, z) ? passesLight(ids[x + size * (z + size * y)] ?? BLOCK_AIR) : false,
  );

  const light = new Uint8Array(size * size * size);
  for (const [packed, level] of lit) {
    const { x, y, z } = unpackCell(packed);
    if (!inside(x, y, z)) continue;
    light[x + size * (z + size * y)] = level;
  }
  return light;
}

/** One thing that burns, in whatever coordinates the caller is using. */
export interface Emitter {
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly level: number;
}

/**
 * Every emitter in a cube of blocks.
 *
 * ⚠️ **One pass and no allocation unless something is burning**, which is by
 * far the commonest case: most chunks in Iceland contain no fire at all.
 */
export function findEmitters(ids: Uint16Array, size: number): Emitter[] {
  const out: Emitter[] = [];
  const cells = size * size * size;
  for (let i = 0; i < cells; i++) {
    /* eslint-disable-next-line security/detect-object-injection --
       A loop counter reading a Uint16Array. Same reasoning as textures.ts. */
    const level = lightFrom(ids[i] ?? BLOCK_AIR);
    if (level === 0) continue;
    out.push({
      x: i % size,
      y: Math.floor(i / (size * size)),
      z: Math.floor(i / size) % size,
      level,
    });
  }
  return out;
}

/**
 * 🔴 **Where a cell's light lives, packed into one number.**
 *
 * Coordinates may be NEGATIVE — a torch outside a chunk lights inward — so the
 * pack is offset. ⚠️ **The range is −{@link PACK_OFFSET} to 255 − offset on
 * each axis**, which is far wider than {@link MAX_LIGHT} can ever reach out of a
 * 32-block chunk. A caller that wandered outside it would silently collide two
 * cells, so the bound is stated here rather than assumed.
 */
const PACK_OFFSET = 64;
const PACK_STRIDE = 256;

export function packCell(x: number, y: number, z: number): number {
  return x + PACK_OFFSET + PACK_STRIDE * (z + PACK_OFFSET + PACK_STRIDE * (y + PACK_OFFSET));
}

function unpackCell(packed: number): { x: number; y: number; z: number } {
  const x = (packed % PACK_STRIDE) - PACK_OFFSET;
  const rest = Math.floor(packed / PACK_STRIDE);
  const z = (rest % PACK_STRIDE) - PACK_OFFSET;
  const y = Math.floor(rest / PACK_STRIDE) - PACK_OFFSET;
  return { x, y, z };
}

/**
 * 🔦 **Flood outward from a handful of fires, and touch nothing else.**
 *
 * 🔴 **THIS SHAPE IS A PERFORMANCE FIX, AND haugur FOUND THE PROBLEM ON DEV:**
 * *"framrate on dev has dropped to 8, framrate on staging is fine."* The first
 * version filled a **62³ array — 238,328 cells — on every single chunk
 * rebuild**, most of them by calling back into the caller for blocks in
 * neighbouring chunks. **It did that whether anything was burning or not.**
 *
 * ✅ **Fire is rare and its reach is small.** A light of level *n* can only ever
 * touch the cells within *n* of it — about 2n³/3, so **roughly 1,100 cells for
 * a hearth and 340 for a torch.** Walking outward from the fires costs that;
 * walking the whole region costs 238,328 regardless.
 *
 * ⚠️ **A `Map` rather than an array**, deliberately: the answer is sparse, and
 * the common case — nothing burning — must allocate nothing at all.
 *
 * @param passes may be asked about cells outside whatever region the caller
 *   cares about; answering `false` there simply stops the light.
 */
export function floodFrom(
  emitters: readonly Emitter[],
  passes: (x: number, y: number, z: number) => boolean,
): Map<number, number> {
  const light = new Map<number, number>();
  if (emitters.length === 0) return light;

  let queue: Emitter[] = [];
  for (const e of emitters) {
    const key = packCell(e.x, e.y, e.z);
    if (e.level <= (light.get(key) ?? 0)) continue;
    light.set(key, e.level);
    queue.push(e);
  }

  while (queue.length > 0) {
    const next: Emitter[] = [];
    for (const cell of queue) {
      const level = light.get(packCell(cell.x, cell.y, cell.z)) ?? 0;
      if (level <= 1) continue;
      for (const [dx, dy, dz] of NEIGHBOURS) {
        const x = cell.x + dx;
        const y = cell.y + dy;
        const z = cell.z + dz;
        if (!passes(x, y, z)) continue;
        const key = packCell(x, y, z);
        /* 🔴 Queue only what got BRIGHTER. Without this the frontier revisits
           cells a stronger light has already settled, which is §4's 7×. */
        if (level - 1 <= (light.get(key) ?? 0)) continue;
        light.set(key, level - 1);
        next.push({ x, y, z, level: level - 1 });
      }
    }
    queue = next;
  }

  return light;
}

/** The six face neighbours. Light turns corners by going round them, never through. */
const NEIGHBOURS: readonly (readonly [number, number, number])[] = [
  [1, 0, 0],
  [-1, 0, 0],
  [0, 1, 0],
  [0, -1, 0],
  [0, 0, 1],
  [0, 0, -1],
];
